import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { uploadFile } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const TONES = ["professionnel", "chaleureux", "premium", "humoristique", "pédagogique", "accessible", "dynamique", "institutionnel"];
const NETWORKS = ["instagram", "facebook", "linkedin", "tiktok", "pinterest"];

function ListEditor({ label, hint, value, onChange, testid }) {
  const [item, setItem] = useState("");
  const add = () => {
    if (!item.trim()) return;
    onChange([...(value || []), item.trim()]);
    setItem("");
  };
  return (
    <div>
      <Label>{label}</Label>
      {hint && <p className="text-xs text-neutral-400 mt-0.5">{hint}</p>}
      <div className="flex gap-2 mt-1.5">
        <Input data-testid={`${testid}-input`} value={item} onChange={(e) => setItem(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Ajouter…" />
        <Button type="button" variant="outline" size="icon" data-testid={`${testid}-add`} onClick={add}><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {(value || []).map((v, i) => (
          <span key={i} className="inline-flex items-center gap-1 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1 text-xs">
            {v}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-neutral-900"><X className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function BrandForm({ client, onSaved }) {
  const [brand, setBrand] = useState({ ...client.brand });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setBrand((b) => ({ ...b, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clients/${client.id}`, { brand });
      toast.success("Identité de marque enregistrée — l'IA l'utilisera pour toutes ses générations");
      onSaved?.();
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await uploadFile(file);
      set("logo_url", data.url);
      toast.success("Logo téléversé — pensez à enregistrer");
    } catch { toast.error("Échec du téléversement"); }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="brand-form">
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Informations générales</h3>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-xl flex items-center justify-center text-white font-heading font-bold text-2xl border border-neutral-200"
            style={{ background: client.color }}>
            {brand.logo_url ? <img src={brand.logo_url} className="w-full h-full object-cover rounded-xl" alt="logo" /> : client.name[0]}
          </div>
          <div>
            <Label htmlFor="logo-upload" className="cursor-pointer text-sm text-neutral-900 font-medium hover:underline" data-testid="brand-logo-upload">
              Téléverser un logo
            </Label>
            <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
            <p className="text-xs text-neutral-400 mt-0.5">PNG ou JPG, fond transparent conseillé</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Site internet</Label><Input data-testid="brand-website" className="mt-1.5" value={brand.website || ""} onChange={(e) => set("website", e.target.value)} /></div>
          <div><Label>Adresse</Label><Input data-testid="brand-address" className="mt-1.5" value={brand.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
          <div><Label>Zone géographique</Label><Input data-testid="brand-zone" className="mt-1.5" value={brand.zone || ""} onChange={(e) => set("zone", e.target.value)} placeholder="Ex : Annecy et alentours" /></div>
          <div>
            <Label>Réseaux sociaux</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {NETWORKS.map((n) => (
                <button key={n} type="button" data-testid={`brand-network-${n}`}
                  onClick={() => set("networks", (brand.networks || []).includes(n) ? brand.networks.filter((x) => x !== n) : [...(brand.networks || []), n])}
                  className={`text-xs px-3 py-1.5 rounded-full border capitalize transition-colors ${
                    (brand.networks || []).includes(n) ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 text-neutral-600"}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-4">
        <h3 className="font-heading font-bold text-lg">Couleurs</h3>
        <div className="flex flex-wrap items-center gap-3">
          {(brand.colors || []).map((c, i) => (
            <div key={i} className="flex items-center gap-1.5 border border-neutral-200 rounded-md px-2 py-1.5">
              <input type="color" value={c} data-testid={`brand-color-${i}`}
                onChange={(e) => set("colors", brand.colors.map((x, j) => (j === i ? e.target.value : x)))}
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent" />
              <span className="text-xs font-mono text-neutral-500">{c}</span>
              <button onClick={() => set("colors", brand.colors.filter((_, j) => j !== i))} className="text-neutral-400 hover:text-neutral-900"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" data-testid="brand-add-color"
            onClick={() => set("colors", [...(brand.colors || []), "#395963"])}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Couleur
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Typographie principale</Label><Input data-testid="brand-font-primary" className="mt-1.5" value={brand.fonts?.primary || ""} onChange={(e) => set("fonts", { ...brand.fonts, primary: e.target.value })} placeholder="Ex : Playfair Display" /></div>
          <div><Label>Typographie secondaire</Label><Input data-testid="brand-font-secondary" className="mt-1.5" value={brand.fonts?.secondary || ""} onChange={(e) => set("fonts", { ...brand.fonts, secondary: e.target.value })} placeholder="Ex : Inter" /></div>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-5">
        <h3 className="font-heading font-bold text-lg">Positionnement & ton</h3>
        <ListEditor label="Valeurs" testid="brand-values" value={brand.values} onChange={(v) => set("values", v)} />
        <div><Label>Positionnement</Label><Textarea data-testid="brand-positioning" className="mt-1.5" rows={3} value={brand.positioning || ""} onChange={(e) => set("positioning", e.target.value)} /></div>
        <ListEditor label="Cibles / personas" testid="brand-personas" value={brand.personas} onChange={(v) => set("personas", v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Ton éditorial</Label>
            <Select value={brand.tone || "professionnel"} onValueChange={(v) => set("tone", v)}>
              <SelectTrigger data-testid="brand-tone" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Précision sur le ton (optionnel)</Label><Input data-testid="brand-tone-custom" className="mt-1.5" value={brand.tone_custom || ""} onChange={(e) => set("tone_custom", e.target.value)} placeholder="Ex : chaleureux mais expert" /></div>
        </div>
        <ListEditor label="Expressions à utiliser" testid="brand-expr-use" value={brand.expressions_use} onChange={(v) => set("expressions_use", v)} />
        <ListEditor label="Expressions interdites" hint="L'IA ne les utilisera jamais." testid="brand-expr-avoid" value={brand.expressions_avoid} onChange={(v) => set("expressions_avoid", v)} />
        <ListEditor label="Messages clés" testid="brand-messages" value={brand.key_messages} onChange={(v) => set("key_messages", v)} />
        <ListEditor label="Call-to-actions réguliers" testid="brand-ctas" value={brand.ctas} onChange={(v) => set("ctas", v)} />
        <ListEditor label="Hashtags récurrents" testid="brand-hashtags" value={brand.hashtags} onChange={(v) => set("hashtags", v)} />
        <ListEditor label="Mentions légales récurrentes" testid="brand-legal" value={brand.legal_mentions} onChange={(v) => set("legal_mentions", v)} />
      </div>

      <Button data-testid="brand-save" onClick={save} disabled={saving}
        className="bg-neutral-900 hover:bg-neutral-800 text-white px-6">
        {saving ? "Enregistrement…" : "Enregistrer l'identité de marque"}
      </Button>
    </div>
  );
}
